import React, { useCallback, useEffect, useRef, useState } from "react";
import Head from "../../../../layout/head/Head";
import { Block, BlockHead, BlockTitle, Button, Col } from "../../../../components/Component";
import { Alert, Card, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from "reactstrap";
import CardTabs from "../../components/cards/CardTabs";
import { useParams } from "react-router";
import Content from "../../../../layout/content/Content";
import { useDispatch, useSelector } from "react-redux";
import { employee_dashboard_actions_helper } from "../../../../actions/actions_helper";
import {
  fail_retrieve_specific_branch_location,
  loading_retrieve_specific_branch_location,
  reset_retrieve_specific_branch_location,
  success_retrieve_specific_branch_location,
} from "../../../../reducers/dashboard_reducers/branch_locations/retrieve_specific_branch_location_Slice";
import {
  ADD_BRANCH_LOCATION_WEIGH_BRIDGE_INTEGRATION_STATE_API,
  PREVIEW_BRANCH_WEIGH_BRIDGE_INTEGRATION_TICKETS_SYNC_API,
  IMPORT_BRANCH_WEIGH_BRIDGE_INTEGRATION_TICKETS_SYNC_API,
  RETRIEVE_SPECIFIC_BRANCH_LOCATION_API,
  RETRIEVE_SYNC_BRANCH_LOCATION_WEIGH_BRIDGE_INTEGRATION_DATA_API,
} from "../../../../api/api_data";
import {
  RETIREVE_SPECIFIC_BRANCH_LOCATION_ACCESS_DENIED_MSG,
  RETIREVE_SPECIFIC_BRANCH_LOCATION_ERR_MSG,
  RETIREVE_SPECIFIC_BRANCH_LOCATION_SUCCESS_MSG,
} from "../../../../constants/success_error_messages_consts";
import * as app_consts from "../../../../constants/react_app_consts";
import { messageToast } from "../../../../utils/Utils";
import ProfileDetailText from "../../components/text/ProfileDetailText";
import PageHeading from "../../components/page_heading/PageHeading";
import ReadOnlyTable from "../../components/table/ReadOnlyTable";
import ModalAdd from "../../components/modal/modal_add/ModalAdd";
import { capitalizeWords, findModuleAction } from "../../../../utils/functionalUtils";
import { makeEmployeeDashboardApiRequest } from "../../../../api/api_helper";
import { sanitizeWeighbridgePreviewCsvFile } from "../../../../utils/weighbridgePreviewCsv";

const storage_type = app_consts.SPECIFIC_BRANCH_LOCATION_STORAGES;
const specific_employee_type = app_consts.SPECIFIC_BRANCH_EMPLOYEES;

const initial_actions_data = {
  assign_branch_location: false,
};

const LOCAL_AGENT_BASE_URL = process.env.REACT_APP_WEIGHBRIDGE_AGENT_URL || "http://localhost:4002";
const DEFAULT_AUTO_SYNC_INTERVAL_MS = 60000;
const autoSyncIntervalOptions = [
  { label: "30 seconds", value: 30000 },
  { label: "1 minute", value: 60000 },
  { label: "5 minutes", value: 300000 },
  { label: "15 minutes", value: 900000 },
];

const previewCsvFieldGuide = [
  { key: "weigh_bridge_ticket_number", required: "Yes", type: "Integer" },
  { key: "weigh_bridge_ticket_reference_no", required: "Yes", type: "Integer" },
  { key: "truck_licence_plate", required: "Yes", type: "String" },
  { key: "transporter_code", required: "Yes", type: "Integer/String" },
  { key: "transporter_name", required: "Yes", type: "String" },
  { key: "driver_contact_id", required: "No", type: "String" },
  { key: "truck_driver_name", required: "No", type: "String" },
  { key: "product_code", required: "Yes", type: "Integer/String" },
  { key: "product_name", required: "Yes", type: "String" },
  { key: "comments", required: "Yes", type: "Text" },
  { key: "trailer_one", required: "No", type: "String" },
  { key: "trailer_two", required: "No", type: "String" },
  { key: "first_weight", required: "Yes", type: "Decimal (kg)" },
  { key: "second_weight", required: "Yes", type: "Decimal (kg)" },
  { key: "net_weight", required: "Yes", type: "Decimal (kg)" },
  { key: "time_in", required: "Yes", type: "UTC datetime" },
  { key: "time_out", required: "No", type: "UTC datetime" },
  { key: "first_weight_user", required: "Yes", type: "Integer" },
  { key: "second_weight_user", required: "No", type: "Integer" },
  { key: "first_weight_workstation", required: "Yes", type: "Integer" },
  { key: "second_weight_workstation", required: "No", type: "Integer" },
];

const previewModalSteps = [
  { id: 1, title: "Branch & Agent", description: "Review branch context and detect the local agent" },
  { id: 2, title: "Upload", description: "Choose the integration and CSV source" },
  { id: 3, title: "Requirements", description: "Review required headers and formats" },
  { id: 4, title: "Results", description: "Inspect valid and invalid remapped tickets" },
];

const getIntegrationIdentifier = (integration = {}) => {
  const identifierCandidates = [
    integration?.integration_id,
    integration?.branch_wbdge_integration_state_id,
    integration?.branch_integration_id,
    integration?.integration_state_id,
    integration?.id,
  ];

  const resolvedIdentifier = identifierCandidates.find(
    (value) => value !== null && value !== undefined && value !== "",
  );

  return resolvedIdentifier || "--";
};

const buildWeighbridgeIntegrationFormData = ({
  integration = {},
  csvFile,
  fallbackBranchLocationId = "",
} = {}) => {
  const formData = new FormData();
  const integrationIdentifier = getIntegrationIdentifier(integration);
  const resolvedBranchLocationId =
    integration?.branch_location_id || integration?.branch_location?.branch_location_id || fallbackBranchLocationId || "";

  formData.append("integration_name", integration?.integration_name || "");

  if (integration?.system) {
    formData.append("system", integration.system);
  }

  if (resolvedBranchLocationId !== "") {
    formData.append("branch_location_id", String(resolvedBranchLocationId));
  }

  if (integrationIdentifier !== "--") {
    formData.append("integration_id", String(integrationIdentifier));
    formData.append("branch_wbdge_integration_state_id", String(integrationIdentifier));
  }

  if (csvFile) {
    formData.append("document", csvFile, csvFile?.name || "weighbridge_tickets.csv");
  }

  return formData;
};

const formatIntegrationOptionLabel = (option = {}) => {
  const integrationName = option?.integration_name || "--";
  const integrationIdentifier = getIntegrationIdentifier(option);

  if (integrationIdentifier === "--") return integrationName;

  return `${integrationName} (${integrationIdentifier})`;
};

const resolveMatchedIntegration = (agentInfo = {}, integrations = []) => {
  if (!Array.isArray(integrations) || integrations.length === 0)
  {
    return null;
  }

  const agentReferenceCandidates = [
    agentInfo?.integration_reference_number,
    agentInfo?.integration_name,
    agentInfo?.reference_number,
    agentInfo?.agent_reference_number,
  ].filter(Boolean);

  if (agentReferenceCandidates.length === 0)
  {
    return null;
  }

  return (
    integrations.find((integration) =>
      agentReferenceCandidates.some((candidate) => candidate === integration?.integration_name),
    ) || null
  );
};

const extractPreviewPayload = (responseData = {}) => {
  if (responseData?.data && typeof responseData.data === "object")
  {
    return responseData.data;
  }

  return responseData;
};

const getPreviewTickets = (data) => {
  if (Array.isArray(data))
  {
    return data;
  }

  if (Array.isArray(data?.tickets))
  {
    return data.tickets;
  }

  return [];
};

const formatPreviewValue = (value) => {
  if (value === null || value === undefined || value === "")
  {
    return "--";
  }

  if (Array.isArray(value))
  {
    return value.length > 0 ? value.join(", ") : "--";
  }

  if (typeof value === "object")
  {
    return JSON.stringify(value);
  }

  return String(value);
};

const normalizeDateValue = (value) => {
  if (!value)
  {
    return null;
  }

  const parsedDate = new Date(value);

  if (Number.isNaN(parsedDate.getTime()))
  {
    return null;
  }

  return parsedDate;
};

const formatAutomationTimestamp = (value) => {
  const parsedDate = normalizeDateValue(value);

  if (!parsedDate)
  {
    return "--";
  }

  return parsedDate.toLocaleString();
};

const formatPreviewDateTimeValue = (value) => {
  const formattedDate = formatAutomationTimestamp(value);

  if (formattedDate !== "--")
  {
    return formattedDate;
  }

  return formatPreviewValue(value);
};

const SpecificBranchLocation = () => {
  const { branch_location_id, branch_area } = useParams();
  const [sideBar, setSidebar] = useState(false);
  const [activeCardTab, setActiveCardTab] = useState("Branch Location Details");
  const [dataFetched, setDataFetched] = useState(false);
  const dispatch = useDispatch();

  const [page_data, setPageData] = useState();
  const [storage_data, setStorageData] = useState([]);
  const [employee_data, setEmployeeData] = useState([]);

  const [openAddModal, setOpenAddModal] = useState(false);
  const [openStorageTypeModal, setOpenStorageTypeModal] = useState(false);
  const [openAddEmployeeModal, setOpenAddEmployeeModal] = useState(false);

  const [account_actions, setAccountActions] = useState([]);
  const [componentData, setComponentData] = useState(initial_actions_data);

  // Integrations state
  const [openIntegrationModal, setOpenIntegrationModal] = useState(false);
  const [integrationLoading, setIntegrationLoading] = useState(false);
  const [integrationError, setIntegrationError] = useState("");
  const [integrationOptions, setIntegrationOptions] = useState([]);
  const [selectedIntegrationName, setSelectedIntegrationName] = useState("");
  const [selectedIntegrationSystem, setSelectedIntegrationSystem] = useState("");
  const [openPreviewModal, setOpenPreviewModal] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [previewFile, setPreviewFile] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [selectedPreviewIntegrationName, setSelectedPreviewIntegrationName] = useState("");
  const [previewModalPage, setPreviewModalPage] = useState(1);
  const [agentLoading, setAgentLoading] = useState(false);
  const [agentError, setAgentError] = useState("");
  const [agentData, setAgentData] = useState(null);
  const [matchedIntegration, setMatchedIntegration] = useState(null);
  const [previewCompleted, setPreviewCompleted] = useState(false);

  // Sync/Import state
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncError, setSyncError] = useState("");
  const [syncData, setSyncData] = useState(null);
  const [openSyncConfirmModal, setOpenSyncConfirmModal] = useState(false);
  const [syncCompleted, setSyncCompleted] = useState(false);
  const [autoSyncEnabled, setAutoSyncEnabled] = useState(true);
  const [autoSyncIntervalMs, setAutoSyncIntervalMs] = useState(DEFAULT_AUTO_SYNC_INTERVAL_MS);
  const [autoSyncRunning, setAutoSyncRunning] = useState(false);
  const [autoSyncStatus, setAutoSyncStatus] = useState("Automation is idle.");
  const [autoSyncLastRunAt, setAutoSyncLastRunAt] = useState(null);
  const [autoSyncLastSuccessAt, setAutoSyncLastSuccessAt] = useState(null);
  const autoSyncTimerRef = useRef(null);
  const autoSyncInProgressRef = useRef(false);

  const toggle = () => {
    setSidebar(!sideBar);
  };

  useEffect(() => {
    sideBar ? document.body.classList.add("toggle-shown") : document.body.classList.remove("toggle-shown");
  }, [sideBar]);

  const tabs = [
    { tabName: "Branch Location Details" },
    { tabName: "Storage Information" },
    { tabName: "Employees" },
    { tabName: "Integrations" },
  ];

  useEffect(() => {
    dispatch(
      employee_dashboard_actions_helper(
        loading_retrieve_specific_branch_location,
        success_retrieve_specific_branch_location,
        fail_retrieve_specific_branch_location,
        RETIREVE_SPECIFIC_BRANCH_LOCATION_ERR_MSG,
        RETIREVE_SPECIFIC_BRANCH_LOCATION_SUCCESS_MSG,
        app_consts.POST,
        RETRIEVE_SPECIFIC_BRANCH_LOCATION_API,
        { branch_location_id: branch_location_id },
        RETIREVE_SPECIFIC_BRANCH_LOCATION_ACCESS_DENIED_MSG,
      ),
    );

    if (localStorage.getItem("log_cred")) {
      setAccountActions(JSON.parse(localStorage.getItem("log_cred"))?.user_actions);
    }
  }, [branch_location_id, dispatch]);

  const retrieve_branch_location_listener = useSelector(
    (state) => state.retrieve_specific_branch_location.retrieve_specific_branch_location,
  );

  useEffect(() => {
    if (
      retrieve_branch_location_listener.status !== app_consts.IDLE &&
      retrieve_branch_location_listener.status !== app_consts.LOADING
    ) {
      if (retrieve_branch_location_listener.status === app_consts.FAIL) {
        messageToast(retrieve_branch_location_listener.message, app_consts.FAIL);
      } else if (retrieve_branch_location_listener.status === app_consts.SUCCESS) {
        setPageData(retrieve_branch_location_listener.data);
      }
      dispatch(reset_retrieve_specific_branch_location());
    }
  }, [retrieve_branch_location_listener, dispatch]);

  useEffect(() => {
    if (account_actions) {
      let actions_data = initial_actions_data;
      account_actions?.map((module) => {
        if (module.module_name === app_consts.SYSTEM_MANAGEMENT) {
          if (findModuleAction(app_consts.ASSIGN_BRANCH_LOCATION, module)) {
            actions_data.assign_branch_location = true;
          }
        }
        return module;
      });
      setComponentData(actions_data);
    }
  }, [account_actions]);

  useEffect(() => {
    if (page_data) {
      setStorageData(page_data?.warehouse_storages || []);
      setEmployeeData(page_data?.employee_list || []);
    }
  }, [page_data]);

  const refreshBranchLocationDetails = useCallback(() => {
    dispatch(
      employee_dashboard_actions_helper(
        loading_retrieve_specific_branch_location,
        success_retrieve_specific_branch_location,
        fail_retrieve_specific_branch_location,
        RETIREVE_SPECIFIC_BRANCH_LOCATION_ERR_MSG,
        RETIREVE_SPECIFIC_BRANCH_LOCATION_SUCCESS_MSG,
        app_consts.POST,
        RETRIEVE_SPECIFIC_BRANCH_LOCATION_API,
        { branch_location_id: branch_location_id },
        RETIREVE_SPECIFIC_BRANCH_LOCATION_ACCESS_DENIED_MSG,
      ),
    );
  }, [branch_location_id, dispatch]);

  useEffect(() => {
    const firstIntegrationName = page_data?.branch_wbdge_integration_states?.[0]?.integration_name || "";
    setSelectedPreviewIntegrationName(firstIntegrationName);
  }, [page_data]);

  useEffect(() => {
    if (!page_data?.branch_wbdge_integration_states?.length || !agentData)
    {
      setMatchedIntegration(null);
      return;
    }

    const matched = resolveMatchedIntegration(agentData, page_data.branch_wbdge_integration_states);

    setMatchedIntegration(matched);

    if (matched?.integration_name)
    {
      setSelectedPreviewIntegrationName(matched.integration_name);
    }
  }, [agentData, page_data]);

  const openWarehouseStorageModal = () => {
    setOpenAddModal(true);
    setOpenStorageTypeModal(false);
  };

  const openAddWarehouseStorageTypeModal = () => {
    setOpenAddModal(false);
    setOpenStorageTypeModal(true);
  };

  const openAddEmployeeToBranchModal = () => {
    setOpenAddModal(false);
    setOpenAddEmployeeModal(true);
  };

  const successAddFunc = (storage_det = null) => {
    setOpenAddModal(false);
    setStorageData([...(storage_data || []), storage_det]);
  };

  const add_employee_to_branch_success_func = () => {
    dispatch(
      employee_dashboard_actions_helper(
        loading_retrieve_specific_branch_location,
        success_retrieve_specific_branch_location,
        fail_retrieve_specific_branch_location,
        RETIREVE_SPECIFIC_BRANCH_LOCATION_ERR_MSG,
        RETIREVE_SPECIFIC_BRANCH_LOCATION_SUCCESS_MSG,
        app_consts.POST,
        RETRIEVE_SPECIFIC_BRANCH_LOCATION_API,
        { branch_location_id: branch_location_id },
        RETIREVE_SPECIFIC_BRANCH_LOCATION_ACCESS_DENIED_MSG,
      ),
    );
    setOpenAddEmployeeModal(false);
  };

  const closeIntegrationModal = () => {
    setOpenIntegrationModal(false);
    setIntegrationError("");
    setSelectedIntegrationName("");
    setSelectedIntegrationSystem("");
  };

  const closePreviewModal = () => {
    if (autoSyncTimerRef.current)
    {
      clearTimeout(autoSyncTimerRef.current);
      autoSyncTimerRef.current = null;
    }

    autoSyncInProgressRef.current = false;
    setOpenPreviewModal(false);
    setPreviewModalPage(1);
    setPreviewLoading(false);
    setPreviewError("");
    setPreviewFile(null);
    setPreviewData(null);
    setAgentLoading(false);
    setAgentError("");
    setAgentData(null);
    setMatchedIntegration(null);
    setPreviewCompleted(false);
    setSyncLoading(false);
    setSyncError("");
    setSyncData(null);
    setOpenSyncConfirmModal(false);
    setSyncCompleted(false);
    setAutoSyncRunning(false);
    setAutoSyncEnabled(true);
    setAutoSyncIntervalMs(DEFAULT_AUTO_SYNC_INTERVAL_MS);
    setAutoSyncStatus("Automation is idle.");
    setAutoSyncLastRunAt(null);
    setAutoSyncLastSuccessAt(null);
  };

  const loadIntegrationStateData = async () => {
    setIntegrationLoading(true);
    setIntegrationError("");
    setIntegrationOptions([]);
    setSelectedIntegrationName("");
    setSelectedIntegrationSystem("");

    try {
      const response = await makeEmployeeDashboardApiRequest(
        "get",
        RETRIEVE_SYNC_BRANCH_LOCATION_WEIGH_BRIDGE_INTEGRATION_DATA_API,
      );

      const responseData = response?.data?.data;
      const rows = Array.isArray(responseData) ? responseData : [];
      const normalizedRows = rows
        .map((row) => ({
          ...(row?.integration || {}),
          branch_location: row?.branch_location || {},
          branch_location_id: row?.integration?.branch_location_id || row?.branch_location?.branch_location_id,
          system: row?.integration?.system || row?.integration?.integration_name || "",
        }))
        .filter((row) => String(row?.branch_location_id) === String(branch_location_id));

      setIntegrationOptions(normalizedRows);

      if (normalizedRows.length > 0) {
        setSelectedIntegrationName(normalizedRows[0]?.integration_name || "");
        setSelectedIntegrationSystem(normalizedRows[0]?.system || normalizedRows[0]?.integration_name || "");
      }
    } catch (error) {
      const errMsg =
        error?.response?.data?.message ||
        error?.response?.data?.errors?.detail ||
        error?.message ||
        "Failed to retrieve integration state data.";

      setIntegrationError(errMsg);
    } finally {
      setIntegrationLoading(false);
    }
  };

  const openBranchIntegrationModal = async () => {
    setOpenIntegrationModal(true);
    await loadIntegrationStateData();
  };

  const openBranchPreviewModal = () => {
    setPreviewModalPage(1);
    setPreviewError("");
    setPreviewData(null);
    setPreviewFile(null);
    setAgentError("");
    setAgentData(null);
    setMatchedIntegration(null);
    setPreviewCompleted(false);
    setSyncError("");
    setSyncData(null);
    setOpenSyncConfirmModal(false);
    setSyncCompleted(false);
    setAutoSyncEnabled(true);
    setAutoSyncIntervalMs(DEFAULT_AUTO_SYNC_INTERVAL_MS);
    setAutoSyncRunning(false);
    setAutoSyncStatus("Waiting for the first automated sync cycle.");
    setAutoSyncLastRunAt(null);
    setAutoSyncLastSuccessAt(null);
    setOpenPreviewModal(true);
  };

  const handleIntegrationChange = (e) => {
    const value = e.target.value;
    setSelectedIntegrationName(value);

    const selected = integrationOptions.find((item) => item?.integration_name === value);

    setSelectedIntegrationSystem(selected?.system || selected?.integration_name || "");
  };

  const handleIntegrationContinue = async () => {
    if (!selectedIntegrationName || !selectedIntegrationSystem) {
      messageToast("Please select an integration name.", app_consts.FAIL);
      return;
    }

    try {
      setIntegrationLoading(true);
      setIntegrationError("");

      const payload = {
        branch_location_id: Number(branch_location_id),
        system: selectedIntegrationSystem,
        integration_name: selectedIntegrationName,
      };

      const response = await makeEmployeeDashboardApiRequest(
        "post",
        ADD_BRANCH_LOCATION_WEIGH_BRIDGE_INTEGRATION_STATE_API,
        payload,
      );

      messageToast(
        response?.data?.message || "Successfully Added Integration State to Branch Location.",
        app_consts.SUCCESS,
      );

      dispatch(
        employee_dashboard_actions_helper(
          loading_retrieve_specific_branch_location,
          success_retrieve_specific_branch_location,
          fail_retrieve_specific_branch_location,
          RETIREVE_SPECIFIC_BRANCH_LOCATION_ERR_MSG,
          RETIREVE_SPECIFIC_BRANCH_LOCATION_SUCCESS_MSG,
          app_consts.POST,
          RETRIEVE_SPECIFIC_BRANCH_LOCATION_API,
          { branch_location_id: branch_location_id },
          RETIREVE_SPECIFIC_BRANCH_LOCATION_ACCESS_DENIED_MSG,
        ),
      );

      closeIntegrationModal();
    } catch (error) {
      const errMsg =
        error?.response?.data?.message ||
        error?.response?.data?.errors?.detail ||
        error?.message ||
        "Failed to add integration state.";

      setIntegrationError(errMsg);
      messageToast(errMsg, app_consts.FAIL);
    } finally {
      setIntegrationLoading(false);
    }
  };

  const handlePreviewFileChange = async (e) => {
    const file = e.target.files?.[0] || null;

    if (!file)
    {
      setPreviewFile(null);
      return;
    }

    if (!file.name.toLowerCase().endsWith(".csv"))
    {
      setPreviewFile(null);
      setPreviewError("The selected document must be a .csv file.");
      return;
    }

    try
    {
      const sanitizedFile = await sanitizeWeighbridgePreviewCsvFile(file);
      setPreviewError("");
      setPreviewFile(sanitizedFile);
      setPreviewCompleted(false);
    } catch (error)
    {
      setPreviewFile(null);
      setPreviewError(
        error?.message || "The selected CSV file could not be prepared for preview.",
      );
    }
  };

  const detectLocalAgent = useCallback(async ({ throwOnError = false } = {}) => {
    try
    {
      setAgentLoading(true);
      setAgentError("");
      setAgentData(null);

      const response = await fetch(`${LOCAL_AGENT_BASE_URL}/health`, {
        method: "GET",
      });

      if (!response.ok)
      {
        throw new Error("Local agent did not respond successfully.");
      }

      const data = await response.json();
      setAgentData(data);
      return data;
    } catch (error)
    {
      const errMsg =
        error?.message ||
        "Failed to detect the local weighbridge agent. Check that the agent is running and CORS is enabled.";

      setAgentError(errMsg);
      if (throwOnError)
      {
        throw new Error(errMsg);
      }
      return null;
    } finally
    {
      setAgentLoading(false);
    }
  }, []);

  const loadCsvFromAgent = useCallback(async ({
    integrationOverride = null,
    showToast = true,
    throwOnError = false,
  } = {}) => {
    const resolvedIntegration = integrationOverride || matchedIntegration;

    if (!resolvedIntegration)
    {
      const errMsg = "No local agent matched the saved branch integrations.";
      if (showToast)
      {
        messageToast(errMsg, app_consts.FAIL);
      }
      if (throwOnError)
      {
        throw new Error(errMsg);
      }
      return null;
    }

    try
    {
      setAgentLoading(true);
      setAgentError("");

      const response = await fetch(`${LOCAL_AGENT_BASE_URL}/export-csv`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          integration_reference_number: resolvedIntegration?.integration_name,
        }),
      });

      if (!response.ok)
      {
        throw new Error("The local agent could not export CSV data.");
      }

      const contentType = response.headers.get("content-type") || "";

      if (contentType.includes("application/json"))
      {
        const data = await response.json();
        const csvContent = data?.csv_content || data?.csv || "";

        if (!csvContent)
        {
          throw new Error("The local agent returned no CSV content.");
        }

        const csvFile = new File(
          [csvContent],
          data?.file_name || `${resolvedIntegration?.integration_name || "weighbridge_tickets"}.csv`,
          { type: "text/csv" },
        );
        const sanitizedFile = await sanitizeWeighbridgePreviewCsvFile(csvFile);

        setPreviewFile(sanitizedFile);
        if (showToast)
        {
          messageToast("CSV data loaded from the local weighbridge agent.", app_consts.SUCCESS);
        }
        return sanitizedFile;
      } else
      {
        const blob = await response.blob();
        const csvFile = new File(
          [blob],
          `${resolvedIntegration?.integration_name || "weighbridge_tickets"}.csv`,
          { type: "text/csv" },
        );
        const sanitizedFile = await sanitizeWeighbridgePreviewCsvFile(csvFile);

        setPreviewFile(sanitizedFile);
        if (showToast)
        {
          messageToast("CSV data loaded from the local weighbridge agent.", app_consts.SUCCESS);
        }
        return sanitizedFile;
      }
    } catch (error)
    {
      const errMsg =
        error?.message ||
        "Failed to load CSV from the local weighbridge agent.";

      setAgentError(errMsg);
      if (showToast)
      {
        messageToast(errMsg, app_consts.FAIL);
      }
      if (throwOnError)
      {
        throw new Error(errMsg);
      }
      return null;
    } finally
    {
      setPreviewCompleted(false);
      setAgentLoading(false);
    }
  }, [matchedIntegration]);

  const handlePreviewTicketsSync = useCallback(async ({
    integrationNameOverride = "",
    fileOverride = null,
    showToast = true,
    throwOnError = false,
  } = {}) => {
    const resolvedIntegrationName = integrationNameOverride || selectedPreviewIntegrationName;
    const resolvedPreviewFile = fileOverride || previewFile;
    const resolvedIntegration =
      page_data?.branch_wbdge_integration_states?.find(
        (integration) => integration?.integration_name === resolvedIntegrationName,
      ) || null;

    if (!resolvedIntegrationName)
    {
      const errMsg = "Please select an integration name.";
      if (showToast)
      {
        messageToast(errMsg, app_consts.FAIL);
      }
      if (throwOnError)
      {
        throw new Error(errMsg);
      }
      return null;
    }

    if (!resolvedPreviewFile)
    {
      const errMsg = "Please select a CSV document to preview.";
      if (showToast)
      {
        messageToast(errMsg, app_consts.FAIL);
      }
      if (throwOnError)
      {
        throw new Error(errMsg);
      }
      return null;
    }

    if (!resolvedIntegration)
    {
      const errMsg = "The selected integration details could not be resolved.";
      if (showToast)
      {
        messageToast(errMsg, app_consts.FAIL);
      }
      if (throwOnError)
      {
        throw new Error(errMsg);
      }
      return null;
    }

    const sanitizedPreviewFile = await sanitizeWeighbridgePreviewCsvFile(resolvedPreviewFile);
    setPreviewFile(sanitizedPreviewFile);

    const formData = buildWeighbridgeIntegrationFormData({
      integration: resolvedIntegration,
      csvFile: sanitizedPreviewFile,
      fallbackBranchLocationId: branch_location_id,
    });

    try
    {
      setPreviewLoading(true);
      setPreviewError("");
      setPreviewData(null);

      const response = await makeEmployeeDashboardApiRequest(
        "post",
        PREVIEW_BRANCH_WEIGH_BRIDGE_INTEGRATION_TICKETS_SYNC_API,
        formData,
      );

      const resolvedPreviewData = extractPreviewPayload(response?.data);
      setPreviewData(resolvedPreviewData);
      setPreviewCompleted(true);
      setPreviewModalPage(4);

      if (showToast)
      {
        messageToast(
          response?.data?.message || "Preview Branch WeighBridge Integration Tickets Sync Data Processed Successfully.",
          app_consts.SUCCESS,
        );
      }
      return resolvedPreviewData;
    } catch (error)
    {
      const errMsg =
        error?.response?.data?.message ||
        error?.response?.data?.errors?.detail ||
        error?.message ||
        "Failed to preview weighbridge integration tickets sync data.";

      setPreviewError(errMsg);
      setPreviewCompleted(false);
      if (showToast)
      {
        messageToast(errMsg, app_consts.FAIL);
      }
      if (throwOnError)
      {
        throw new Error(errMsg);
      }
      return null;
    } finally
    {
      setPreviewLoading(false);
    }
  }, [page_data, previewFile, selectedPreviewIntegrationName]);

  const renderPreviewTable = (tickets = [], emptyMessage = "No tickets found.") => (
    <div className="table-responsive border rounded">
      <table className="table table-middle mb-0">
        <thead className="table-light">
          <tr>
            <th>Reference No.</th>
            <th>Ticket No.</th>
            <th>Truck Plate</th>
            <th>Product</th>
            <th>Time In</th>
            <th>Net Weight</th>
            <th>Probable Batch ID</th>
            <th>Status</th>
            <th>Errors</th>
          </tr>
        </thead>
        <tbody>
          {tickets.length > 0 ? (
            tickets.map((item, index) => {
              const ticketData = item?.tickets || {};
              const ticketErrors = Array.isArray(item?.errors) ? item.errors : [];

              return (
                <tr key={`${ticketData?.weigh_bridge_ticket_reference_no || "ticket"}-${index}`}>
                  <td>{formatPreviewValue(ticketData?.weigh_bridge_ticket_reference_no)}</td>
                  <td>{formatPreviewValue(ticketData?.weigh_bridge_ticket_number)}</td>
                  <td>{formatPreviewValue(ticketData?.truck_licence_plate)}</td>
                  <td>{formatPreviewValue(ticketData?.product_name || ticketData?.product_code)}</td>
                  <td>{formatPreviewDateTimeValue(ticketData?.time_in)}</td>
                  <td>{formatPreviewValue(ticketData?.net_weight)}</td>
                  <td>{formatPreviewValue(ticketData?.probable_sync_batch_id)}</td>
                  <td>
                    <span className={`badge ${ticketData?.valid ? "bg-success" : "bg-danger"}`}>
                      {ticketData?.valid ? "Valid" : "Invalid"}
                    </span>
                  </td>
                  <td>{ticketErrors.length > 0 ? ticketErrors.join(", ") : "--"}</td>
                </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan="9" className="text-center text-soft py-3">
                {emptyMessage}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );

  const previewSummaryCard = (label, value, textClassName = "") => (
    <Card className="card-bordered h-100 shadow-sm">
      <div className="card-inner">
        <div className="text-soft small text-uppercase mb-1">{label}</div>
        <div className={`fs-4 fw-semibold ${textClassName}`.trim()}>{formatPreviewValue(value)}</div>
      </div>
    </Card>
  );

  const handleOpenSyncConfirmModal = () => {
    if (!previewData?.total_valid_tickets_count)
    {
      messageToast("No valid tickets to sync.", app_consts.FAIL);
      return;
    }
    setOpenSyncConfirmModal(true);
  };

  const handleCloseSyncConfirmModal = () => {
    setOpenSyncConfirmModal(false);
    setSyncError("");
  };

  const handleConfirmSyncTickets = useCallback(async ({
    integrationNameOverride = "",
    fileOverride = null,
    showToast = true,
    throwOnError = false,
  } = {}) => {
    const resolvedIntegrationName = integrationNameOverride || selectedPreviewIntegrationName;
    const resolvedPreviewFile = fileOverride || previewFile;
    const resolvedIntegration =
      page_data?.branch_wbdge_integration_states?.find(
        (integration) => integration?.integration_name === resolvedIntegrationName,
      ) || null;

    if (!resolvedIntegrationName)
    {
      const errMsg = "Please select an integration name.";
      if (showToast)
      {
        messageToast(errMsg, app_consts.FAIL);
      }
      if (throwOnError)
      {
        throw new Error(errMsg);
      }
      return null;
    }

    if (!resolvedPreviewFile)
    {
      const errMsg = "No CSV document available for sync.";
      if (showToast)
      {
        messageToast(errMsg, app_consts.FAIL);
      }
      if (throwOnError)
      {
        throw new Error(errMsg);
      }
      return null;
    }

    if (!resolvedIntegration)
    {
      const errMsg = "The selected integration details could not be resolved.";
      if (showToast)
      {
        messageToast(errMsg, app_consts.FAIL);
      }
      if (throwOnError)
      {
        throw new Error(errMsg);
      }
      return null;
    }

    const sanitizedPreviewFile = await sanitizeWeighbridgePreviewCsvFile(resolvedPreviewFile);
    setPreviewFile(sanitizedPreviewFile);

    const formData = buildWeighbridgeIntegrationFormData({
      integration: resolvedIntegration,
      csvFile: sanitizedPreviewFile,
      fallbackBranchLocationId: branch_location_id,
    });

    try
    {
      setSyncLoading(true);
      setSyncError("");
      setSyncData(null);

      const response = await makeEmployeeDashboardApiRequest(
        "post",
        IMPORT_BRANCH_WEIGH_BRIDGE_INTEGRATION_TICKETS_SYNC_API,
        formData,
      );

      const resolvedSyncData = extractPreviewPayload(response?.data);
      setSyncData(resolvedSyncData);
      setSyncCompleted(true);
      setOpenSyncConfirmModal(false);

      refreshBranchLocationDetails();

      if (showToast)
      {
        messageToast(
          response?.data?.message || "Branch WeighBridge Integration Tickets Sync Data Imported Successfully.",
          app_consts.SUCCESS,
        );
      }
      return resolvedSyncData;
    } catch (error)
    {
      const errMsg =
        error?.response?.data?.message ||
        error?.response?.data?.errors?.detail ||
        error?.message ||
        "Failed to import weighbridge integration tickets sync data.";

      setSyncError(errMsg);
      if (showToast)
      {
        messageToast(errMsg, app_consts.FAIL);
      }
      if (throwOnError)
      {
        throw new Error(errMsg);
      }
      return null;
    } finally
    {
      setSyncLoading(false);
    }
  }, [page_data, previewFile, refreshBranchLocationDetails, selectedPreviewIntegrationName]);

  const handleSubmitSyncedTickets = () => {
    handleOpenSyncConfirmModal();
  };

  // Sync results data
  const successCreationTickets = getPreviewTickets(syncData?.success_creation_tickets);
  const failCreationTickets = getPreviewTickets(syncData?.fail_creation_tickets);

  const allTickets = getPreviewTickets(previewData?.all_tickets);
  const validTickets = getPreviewTickets(previewData?.total_valid_tickets);
  const invalidTickets = getPreviewTickets(previewData?.total_invalid_tickets);
  const probableBatchIds = [
    ...new Set(
      allTickets
        .map((item) => item?.tickets?.probable_sync_batch_id)
        .filter((value) => value !== null && value !== undefined && value !== ""),
    ),
  ];
  const selectedPreviewIntegration =
    page_data?.branch_wbdge_integration_states?.find(
      (integration) => integration?.integration_name === selectedPreviewIntegrationName,
    ) || null;
  const selectedPreviewIntegrationLastSyncDate = normalizeDateValue(selectedPreviewIntegration?.last_sync_at);
  const isFirstSyncPreview = !selectedPreviewIntegrationLastSyncDate;
  const hasPreviewIntegrations = Boolean(page_data?.branch_wbdge_integration_states?.length);
  const previewResultsAvailable = Boolean(previewData);

  const runAutomatedSyncCycle = useCallback(async () => {
    if (autoSyncInProgressRef.current || !openPreviewModal || !hasPreviewIntegrations)
    {
      return;
    }

    autoSyncInProgressRef.current = true;
    setAutoSyncRunning(true);
    setAutoSyncLastRunAt(new Date().toISOString());

    try
    {
      setPreviewModalPage(1);
      setAutoSyncStatus("Detecting the local weighbridge agent...");

      const detectedAgent = await detectLocalAgent({ throwOnError: true });
      const resolvedIntegration = resolveMatchedIntegration(
        detectedAgent,
        page_data?.branch_wbdge_integration_states || [],
      );

      if (!resolvedIntegration)
      {
        throw new Error("Detected local agent did not match any saved branch integration.");
      }

      setMatchedIntegration(resolvedIntegration);
      setSelectedPreviewIntegrationName(resolvedIntegration.integration_name || "");

      setPreviewModalPage(2);
      setAutoSyncStatus(`Loading CSV from ${resolvedIntegration.integration_name}...`);
      const csvFile = await loadCsvFromAgent({
        integrationOverride: resolvedIntegration,
        showToast: false,
        throwOnError: true,
      });

      setPreviewModalPage(3);
      setAutoSyncStatus("Running preview checks...");
      await handlePreviewTicketsSync({
        integrationNameOverride: resolvedIntegration.integration_name,
        fileOverride: csvFile,
        showToast: false,
        throwOnError: true,
      });

      setPreviewModalPage(4);
      setAutoSyncStatus("Submitting valid tickets...");
      await handleConfirmSyncTickets({
        integrationNameOverride: resolvedIntegration.integration_name,
        fileOverride: csvFile,
        showToast: false,
        throwOnError: true,
      });

      setAutoSyncLastSuccessAt(new Date().toISOString());
      setAutoSyncStatus("Last automated sync completed successfully.");
    } catch (error)
    {
      setAutoSyncStatus(error?.message || "Automated weighbridge sync failed.");
    } finally
    {
      autoSyncInProgressRef.current = false;
      setAutoSyncRunning(false);
    }
  }, [
    detectLocalAgent,
    handleConfirmSyncTickets,
    handlePreviewTicketsSync,
    hasPreviewIntegrations,
    loadCsvFromAgent,
    openPreviewModal,
    page_data,
  ]);

  useEffect(() => {
    if (autoSyncTimerRef.current)
    {
      clearTimeout(autoSyncTimerRef.current);
      autoSyncTimerRef.current = null;
    }

    if (!openPreviewModal || !hasPreviewIntegrations || !autoSyncEnabled)
    {
      if (!autoSyncRunning)
      {
        setAutoSyncStatus(autoSyncEnabled ? "Waiting for the preview modal to start automation." : "Automation paused.");
      }
      return undefined;
    }

    if (autoSyncRunning)
    {
      return undefined;
    }

    if (!autoSyncRunning)
    {
      setAutoSyncStatus(`Next automated sync starts in ${Math.round(autoSyncIntervalMs / 1000)} seconds.`);
    }

    autoSyncTimerRef.current = setTimeout(() => {
      runAutomatedSyncCycle();
    }, autoSyncIntervalMs);

    return () => {
      if (autoSyncTimerRef.current)
      {
        clearTimeout(autoSyncTimerRef.current);
        autoSyncTimerRef.current = null;
      }
    };
  }, [
    autoSyncEnabled,
    autoSyncIntervalMs,
    autoSyncLastRunAt,
    autoSyncRunning,
    hasPreviewIntegrations,
    openPreviewModal,
    runAutomatedSyncCycle,
  ]);

  const canOpenPreviewStep = (stepId) => {
    if (!hasPreviewIntegrations && stepId > 1)
    {
      return false;
    }

    if (stepId === 4 && !previewResultsAvailable)
    {
      return false;
    }

    return true;
  };

  const goToPreviewStep = (stepId) => {
    if (!canOpenPreviewStep(stepId))
    {
      return;
    }

    setPreviewModalPage(stepId);
  };

  const goToNextPreviewStep = () => {
    const nextStep = Math.min(previewModalPage + 1, previewModalSteps.length);

    if (canOpenPreviewStep(nextStep))
    {
      setPreviewModalPage(nextStep);
    }
  };

  const goToPreviousPreviewStep = () => {
    setPreviewModalPage((prev) => Math.max(prev - 1, 1));
  };

  return (
    <>
      <Head title={`Branch Location Details ${capitalizeWords(branch_area)}`} />
      <Content>
        <PageHeading
          title="Branch Location Details"
          title_addition={capitalizeWords(branch_area)}
          description="Details of Branch Location"
          backToText="Branch Locations"
          backToLink={`/dashboard/admin/branch_locations`}
        />

        <Block>
          <Card>
            <div className="card-aside-wrap" id="user-detail-block">
              <div className="card-content">
                <CardTabs
                  cardTabs={tabs}
                  activeCardTab={activeCardTab}
                  setActiveCardTab={setActiveCardTab}
                  dataFetched={setDataFetched}
                />

                {activeCardTab === "Branch Location Details" && (
                  <div className="card-inner">
                    <Block>
                      <BlockHead>
                        <BlockTitle tag="h5">Branch Location Details</BlockTitle>
                        <p>Location and Contact Information</p>
                      </BlockHead>
                      <div className="profile-ud-list">
                        <ProfileDetailText
                          label="Branch Reference No."
                          text={page_data?.branch?.branch_reference_no || page_data?.branch?.branch_ref_no || "--"}
                        />
                        <ProfileDetailText label="Branch Area" text={page_data?.branch?.branch_area} />
                        <ProfileDetailText label="Physical Address" text={page_data?.branch?.branch_physical_address} />
                        <ProfileDetailText label="Mobile Number" text={page_data?.branch?.branch_mobile_number} />
                        <ProfileDetailText label="Account Status" text={page_data?.branch?.account_status} />
                        <ProfileDetailText label="Province" text={page_data?.branch?.province_name} />
                        <ProfileDetailText label="Country" text={page_data?.branch?.country_name} />
                      </div>
                    </Block>
                  </div>
                )}

                {activeCardTab === "Storage Information" && (
                  <>
                    <div className="card-inner">
                      <Block>
                        <div className="flex-row items-center justify-between">
                          <BlockHead>
                            <BlockTitle tag="h5">Storage Information</BlockTitle>
                            <p>Warehouse Stroage Information</p>
                          </BlockHead>
                          <Col xl="12" className="my-3 mt-1">
                            <div className="form-group">
                              <div className="form-control-wrap-flex-wrap justify-start">
                                <Button
                                  onClick={openWarehouseStorageModal}
                                  className="btn btn-dim btn-outline-primary"
                                  size="md"
                                >
                                  Add Warehouse Storage
                                </Button>
                              </div>
                            </div>
                          </Col>
                        </div>
                        <div className="my-3">
                          <ReadOnlyTable data={storage_data} tableType={storage_type} />
                        </div>
                      </Block>
                    </div>

                    <ModalAdd
                      openModal={openAddModal}
                      setOpenModal={setOpenAddModal}
                      modalTitle="Add Warehouse Storage"
                      modalDescription="Please Complete this form to Add a Warehouse Storage"
                      type={app_consts.ADD_WAREHOUSE_STORAGE}
                      succFunc={successAddFunc}
                      accountId={page_data}
                      openOtherModalFunc={openAddWarehouseStorageTypeModal}
                    />

                    <ModalAdd
                      openModal={openStorageTypeModal}
                      setOpenModal={setOpenStorageTypeModal}
                      modalTitle="Add Warehouse Storage Type"
                      modalDescription="Please Complete this form to Add a Warehouse Storage Type"
                      type={app_consts.ADD_WAREHOUSE_STORAGE_TYPE}
                      succFunc={openWarehouseStorageModal}
                      size="md"
                      openOtherModalFunc={openWarehouseStorageModal}
                    />
                  </>
                )}

                {activeCardTab === "Employees" && (
                  <>
                    <div className="card-inner">
                      <Block>
                        <div className="flex-row items-center justify-between">
                          <BlockHead>
                            <BlockTitle tag="h5">Employees</BlockTitle>
                            <p>Branch Employee Information</p>
                          </BlockHead>
                          {componentData?.assign_branch_location && (
                            <Col xl="12" className="my-3 mt-1">
                              <div className="form-group">
                                <div className="form-control-wrap-flex-wrap justify-start">
                                  <Button
                                    onClick={openAddEmployeeToBranchModal}
                                    className="btn btn-dim btn-outline-primary"
                                    size="md"
                                  >
                                    Add Employee
                                  </Button>
                                </div>
                              </div>
                            </Col>
                          )}
                        </div>
                        <div className="my-3">
                          <ReadOnlyTable data={employee_data} tableType={specific_employee_type} />
                        </div>
                      </Block>
                    </div>

                    <ModalAdd
                      openModal={openAddEmployeeModal}
                      setOpenModal={setOpenAddEmployeeModal}
                      modalTitle={`Add Employe to ${capitalizeWords(branch_area)} Branch`}
                      modalDescription="Please Select the Employee and Employee Role"
                      type={app_consts.ADD_EMPLOYEE_TO_BRANCH}
                      succFunc={add_employee_to_branch_success_func}
                      size="md"
                      accountId={branch_location_id}
                    />
                  </>
                )}

                {activeCardTab === "Integrations" && (
                  <div className="card-inner">
                    <Block>
                      <div className="flex-row items-center justify-between">
                        <BlockHead>
                          <BlockTitle tag="h5">Integrations</BlockTitle>
                          <p>Configure branch integrations using the branch reference number.</p>
                        </BlockHead>

                        <Col xl="12" className="my-3 mt-1">
                          <div className="form-group">
                            <div className="d-flex flex-wrap align-items-center gap-2">
                              <Button
                                className="btn btn-dim btn-outline-primary"
                                size="md"
                                onClick={openBranchIntegrationModal}
                              >
                                Add Integration
                              </Button>
                              <Button
                                className="btn btn-dim btn-outline-info"
                                size="md"
                                onClick={openBranchPreviewModal}
                                disabled={!page_data?.branch_wbdge_integration_states?.length}
                              >
                                Sync WeighBridge Tickets
                              </Button>
                            </div>
                          </div>
                        </Col>
                      </div>

                      {page_data?.branch_wbdge_integration_states?.length > 0 ? (
                        page_data.branch_wbdge_integration_states.map((integration, index) => (
                          <div className="profile-ud-list mb-4" key={integration?.id || index}>
                            <ProfileDetailText
                              label="Branch Reference No."
                              text={page_data?.branch?.branch_ref_no || page_data?.branch?.branch_reference_no || "--"}
                            />
                            <ProfileDetailText
                              label="Integration ID"
                              text={getIntegrationIdentifier(integration)}
                            />
                            <ProfileDetailText label="Integration Name" text={integration?.integration_name || "--"} />
                            <ProfileDetailText
                              label="Last Sync At"
                              text={formatPreviewDateTimeValue(integration?.last_sync_at)}
                            />
                            <ProfileDetailText label="Last Sync Status" text={integration?.last_sync_status || "--"} />
                            <ProfileDetailText
                              label="Records Synced"
                              text={
                                integration?.records_synced !== null && integration?.records_synced !== undefined
                                  ? integration.records_synced
                                  : "--"
                              }
                            />
                            <ProfileDetailText label="Created On" text={integration?.created_on || "--"} />
                          </div>
                        ))
                      ) : (
                        <div className="profile-ud-list">
                          <ProfileDetailText
                            label="Branch Reference No."
                            text={page_data?.branch?.branch_ref_no || page_data?.branch?.branch_reference_no || "--"}
                          />
                          <ProfileDetailText label="Integration ID" text="No integration ID available yet." />
                          <ProfileDetailText label="Integration Status" text="No integrations added yet." />
                        </div>
                      )}
                    </Block>
                  </div>
                )}
              </div>
            </div>
          </Card>
        </Block>
      </Content>

      <Modal isOpen={openIntegrationModal} toggle={closeIntegrationModal} className="modal-dialog-centered" size="md">
        <ModalHeader toggle={closeIntegrationModal}>Add Branch Integration</ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <p className="text-soft mb-2">Retrieve available weighbridge integrations for this branch.</p>
          </div>

          <div className="profile-ud-list mb-3">
            <ProfileDetailText label="Branch Area" text={page_data?.branch?.branch_area || "--"} />
            <ProfileDetailText
              label="Branch Reference No."
              text={page_data?.branch?.branch_reference_no || page_data?.branch?.branch_ref_no || "--"}
            />
          </div>

          {integrationLoading ? (
            <div className="p-3 d-flex justify-content-center align-items-center">
              <Spinner size="sm" className="me-2" />
              <span>Loading integration options...</span>
            </div>
          ) : integrationError ? (
            <Alert color="danger" className="mb-3">
              {integrationError}
            </Alert>
          ) : (
            <>
              <div className="form-group mb-3">
                <label className="form-label">Integration Name</label>
                <div className="form-control-wrap">
                  <select className="form-select" value={selectedIntegrationName} onChange={handleIntegrationChange}>
                    <option value="">Select Integration</option>
                    {integrationOptions.map((option, index) => (
                      <option
                        key={`${option?.integration_name || "integration"}-${index}`}
                        value={option?.integration_name || ""}
                      >
                        {formatIntegrationOptionLabel(option)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group mb-4">
                <label className="form-label">System</label>
                <div className="form-control-wrap">
                  <input type="text" className="form-control" value={selectedIntegrationSystem || ""} readOnly />
                </div>
              </div>

              {integrationOptions.length === 0 && (
                <Alert color="warning" className="mb-3">
                  No integration options available for this branch.
                </Alert>
              )}

              <ModalFooter className="px-0 pb-0">
                <Button color="light" onClick={closeIntegrationModal}>
                  Cancel
                </Button>
                <Button
                  color="primary"
                  onClick={handleIntegrationContinue}
                  disabled={!selectedIntegrationName || integrationLoading}
                >
                  {integrationLoading ? "Saving..." : "Continue"}
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalBody>
      </Modal>

      <Modal isOpen={openPreviewModal} toggle={closePreviewModal} className="modal-dialog-centered" size="xl">
        <ModalHeader toggle={closePreviewModal}>Sync WeighBridge Tickets</ModalHeader>
        <ModalBody>
          <div className="mb-4">
            <p className="text-soft mb-0">
              Detect the local weighbridge agent, retrieve its CSV data, preview valid and invalid tickets, and submit valid rows either manually or on an interval.
            </p>
          </div>

          <Card className="card-bordered shadow-sm mb-4">
            <div className="card-inner">
              <div className="profile-ud-list mb-0">
                <ProfileDetailText label="Branch Area" text={page_data?.branch?.branch_area || "--"} />
                <ProfileDetailText
                  label="Branch Reference No."
                  text={page_data?.branch?.branch_reference_no || page_data?.branch?.branch_ref_no || "--"}
                />
              </div>
            </div>
          </Card>

          <Card className="card-bordered shadow-sm mb-4">
            <div className="card-inner">
              <div className="row g-3 align-items-end">
                <div className="col-lg-4">
                  <label className="form-label d-block">Automated Sync</label>
                  <div className="form-check form-switch">
                    <input
                      id="auto-weighbridge-sync"
                      type="checkbox"
                      className="form-check-input"
                      checked={autoSyncEnabled}
                      onChange={(e) => setAutoSyncEnabled(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="auto-weighbridge-sync">
                      Run detect, load, preview, and submit automatically
                    </label>
                  </div>
                </div>

                <div className="col-lg-3">
                  <label className="form-label">Interval</label>
                  <div className="form-control-wrap">
                    <select
                      className="form-select"
                      value={autoSyncIntervalMs}
                      onChange={(e) => setAutoSyncIntervalMs(Number(e.target.value))}
                      disabled={!autoSyncEnabled || autoSyncRunning}
                    >
                      {autoSyncIntervalOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="col-lg-5">
                  <div className="small text-soft text-uppercase mb-1">Automation Status</div>
                  <div className={`fw-medium ${autoSyncRunning ? "text-primary" : ""}`}>
                    {autoSyncRunning ? "Automated sync in progress..." : autoSyncStatus}
                  </div>
                  <small className="text-soft d-block mt-1">
                    Last run: {formatAutomationTimestamp(autoSyncLastRunAt)}
                  </small>
                  <small className="text-soft d-block">
                    Last success: {formatAutomationTimestamp(autoSyncLastSuccessAt)}
                  </small>
                  <small className="text-soft d-block">
                    Automation runs only while this modal remains open.
                  </small>
                </div>
              </div>

              <div className="d-flex flex-wrap align-items-center gap-2 mt-3">
                <Button
                  color="primary"
                  onClick={runAutomatedSyncCycle}
                  disabled={!hasPreviewIntegrations || autoSyncRunning}
                >
                  {autoSyncRunning ? "Running..." : "Run Now"}
                </Button>
                <small className="text-soft">
                  Manual buttons below still work as a fallback.
                </small>
              </div>
            </div>
          </Card>

          <div className="row g-3 mb-4">
            {previewModalSteps.map((step) => {
              const isActive = previewModalPage === step.id;
              const isEnabled = canOpenPreviewStep(step.id);

              return (
                <div className="col-md-3" key={step.id}>
                  <button
                    type="button"
                    className={`w-100 text-start border rounded p-3 bg-white ${isActive ? "border-primary" : ""}`}
                    onClick={() => goToPreviewStep(step.id)}
                    disabled={!isEnabled}
                    style={{ opacity: isEnabled ? 1 : 0.65 }}
                  >
                    <div className="small text-soft text-uppercase mb-2">Step {step.id}</div>
                    <div className={`fw-medium mb-1 ${isActive ? "text-primary" : ""}`}>{step.title}</div>
                    <div className="small text-soft">{step.description}</div>
                  </button>
                </div>
              );
            })}
          </div>

          {previewError && (
            <Alert color="danger" className="mb-3">
              {previewError}
            </Alert>
          )}

          {!page_data?.branch_wbdge_integration_states?.length ? (
            <Alert color="warning" className="mb-0">
              Add a branch integration first before previewing weighbridge ticket sync data.
            </Alert>
          ) : (
            <>
              {previewModalPage === 1 && (
              <Card className="card-bordered shadow-sm mb-4">
                <div className="card-inner">
                  <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                    <div>
                      <h6 className="mb-1">Local Agent Detection</h6>
                      <p className="text-soft mb-0">
                        Temporary agent endpoint: <code>{LOCAL_AGENT_BASE_URL}</code>
                      </p>
                    </div>
                    <Button
                      color="light"
                      onClick={detectLocalAgent}
                      disabled={agentLoading || autoSyncRunning}
                    >
                      {agentLoading ? "Detecting..." : "Detect Local Agent"}
                    </Button>
                  </div>

                  {agentError && (
                    <Alert color="danger" className="mb-3">
                      {agentError}
                    </Alert>
                  )}

                  <div className="row g-3">
                    <div className="col-md-6">
                      <div className="border rounded p-3 h-100">
                        <div className="small text-soft text-uppercase mb-2">Agent Status</div>
                        <div className="fw-medium">{formatPreviewValue(agentData?.status || (agentData ? "online" : ""))}</div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="border rounded p-3 h-100">
                        <div className="small text-soft text-uppercase mb-2">Agent Reference</div>
                        <div className="fw-medium">
                          {formatPreviewValue(
                            agentData?.integration_reference_number ||
                              agentData?.integration_name ||
                              agentData?.reference_number ||
                              agentData?.agent_reference_number,
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {agentData && (
                    <Alert color={matchedIntegration ? "success" : "warning"} className="mt-3 mb-0">
                      {matchedIntegration
                        ? `Matched local agent to saved integration ${matchedIntegration?.integration_name}.`
                        : "A local agent was detected, but it did not match any saved branch integration."}
                    </Alert>
                  )}
                </div>
              </Card>
              )}

              {previewModalPage === 2 && (
              <>
              <Card className="card-bordered shadow-sm mb-4">
                <div className="card-inner">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <div className="form-group mb-0">
                        <label className="form-label">Integration Name</label>
                        <div className="form-control-wrap">
                          <select
                            className="form-select"
                            value={selectedPreviewIntegrationName}
                            onChange={(e) => setSelectedPreviewIntegrationName(e.target.value)}
                          >
                            <option value="">Select Integration</option>
                            {page_data?.branch_wbdge_integration_states?.map((integration, index) => (
                              <option
                                key={`${integration?.integration_name || "integration"}-${index}`}
                                value={integration?.integration_name || ""}
                              >
                                {formatIntegrationOptionLabel(integration)}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="form-group mb-0">
                        <label className="form-label">CSV Document</label>
                        <div className="form-control-wrap">
                          <input
                            type="file"
                            accept=".csv,text/csv"
                            className="form-control"
                            onChange={handlePreviewFileChange}
                          />
                        </div>
                        <small className="text-soft d-block mt-2">
                          Expected file type: `.csv`
                          {previewFile ? ` | Selected: ${previewFile.name}` : ""}
                        </small>
                      </div>
                    </div>
                  </div>

                  <div className="d-flex flex-wrap align-items-center gap-2 mt-3">
                    <Button
                      color="info"
                      onClick={loadCsvFromAgent}
                      disabled={!matchedIntegration || agentLoading || autoSyncRunning}
                    >
                      {agentLoading ? "Loading CSV..." : "Load CSV From Agent"}
                    </Button>
                    <small className="text-soft">
                      Manual file upload remains available as a fallback.
                    </small>
                  </div>
                </div>
              </Card>

              <Alert color={isFirstSyncPreview ? "info" : "secondary"} className="mb-4">
                {isFirstSyncPreview
                  ? "This appears to be the first sync for the selected integration. Valid historical rows should remain eligible during preview."
                  : `Last sync detected at ${formatPreviewDateTimeValue(
                    selectedPreviewIntegration?.last_sync_at,
                  )}. Valid rows should be checked against that sync time.`}
              </Alert>
              </>
              )}

              {previewModalPage === 3 && (
              <Card className="card-bordered shadow-sm mb-4">
                <div className="card-inner">
                  <h6 className="mb-2">CSV Requirements</h6>
                  <p className="text-soft mb-3">
                    The upload must be a `.csv` file with the expected weighbridge headers and date/weight formats.
                  </p>

                  <div className="row g-3 mb-3">
                    <div className="col-md-4">
                      <div className="border rounded p-3 h-100">
                        <div className="small text-soft text-uppercase mb-2">Date Format</div>
                        <div className="fw-medium">%Y-%m-%d %H:%M:%S</div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="border rounded p-3 h-100">
                        <div className="small text-soft text-uppercase mb-2">Weight Unit</div>
                        <div className="fw-medium">Kilograms in CSV</div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="border rounded p-3 h-100">
                        <div className="small text-soft text-uppercase mb-2">Second Weight Rule</div>
                        <div className="fw-medium">Use `0` if not yet set</div>
                      </div>
                    </div>
                  </div>

                  <div className="table-responsive border rounded">
                    <table className="table table-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Header</th>
                          <th>Required</th>
                          <th>Type</th>
                        </tr>
                      </thead>
                      <tbody>
                        {previewCsvFieldGuide.map((field) => (
                          <tr key={field.key}>
                            <td>
                              <code>{field.key}</code>
                            </td>
                            <td>{field.required}</td>
                            <td>{field.type}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </Card>
              )}

              <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
                <div className="d-flex flex-wrap align-items-center gap-2">
                  <Button
                    color="light"
                    onClick={goToPreviousPreviewStep}
                    disabled={previewModalPage === 1 || previewLoading || autoSyncRunning}
                  >
                    Back
                  </Button>
                  {previewModalPage < 3 && (
                    <Button
                      color="primary"
                      onClick={goToNextPreviewStep}
                      disabled={!canOpenPreviewStep(previewModalPage + 1) || autoSyncRunning}
                    >
                      Next
                    </Button>
                  )}
                </div>

                <div className="d-flex flex-wrap align-items-center gap-2">
                  {previewModalPage === 3 && (
                    <Button
                      color="primary"
                      onClick={handlePreviewTicketsSync}
                      disabled={!selectedPreviewIntegrationName || !previewFile || previewLoading || autoSyncRunning}
                    >
                      {previewLoading ? "Previewing..." : "Run Preview"}
                    </Button>
                  )}
                  {previewModalPage === 4 && previewCompleted && (
                    <Button color="success" onClick={handleSubmitSyncedTickets} disabled={autoSyncRunning}>
                      Submit
                    </Button>
                  )}
                  <Button color="light" onClick={closePreviewModal} disabled={previewLoading || autoSyncRunning}>
                    Close
                  </Button>
                </div>
              </div>

              {previewModalPage === 4 && previewLoading && (
                <div className="p-3 d-flex justify-content-center align-items-center border rounded bg-light mb-4">
                  <Spinner size="sm" className="me-2" />
                  <span>Previewing tickets sync data...</span>
                </div>
              )}

              {previewModalPage === 4 && previewData && (
                <>
                  <div className="row g-3 mb-4">
                    <div className="col-md-3">
                      {previewSummaryCard("Total Tickets", previewData?.total_tickets)}
                    </div>
                    <div className="col-md-3">
                      {previewSummaryCard("Valid Tickets", previewData?.total_valid_tickets_count, "text-success")}
                    </div>
                    <div className="col-md-3">
                      {previewSummaryCard("Invalid Tickets", previewData?.total_invalid_tickets_count, "text-danger")}
                    </div>
                    <div className="col-md-3">
                      {previewSummaryCard(
                        "Probable Batch ID",
                        probableBatchIds.length === 1 ? probableBatchIds[0] : probableBatchIds.join(", "),
                      )}
                    </div>
                  </div>

                  <div className="row g-3 mb-4">
                    <div className="col-md-6">
                      <Card className="card-bordered h-100 shadow-sm">
                        <div className="card-inner">
                          <div className="text-soft small text-uppercase mb-1">Time Range</div>
                          <div className="small">
                            {formatPreviewDateTimeValue(previewData?.time_in_range?.start_date)}
                            <br />
                            {formatPreviewDateTimeValue(previewData?.time_in_range?.end_date)}
                          </div>
                        </div>
                      </Card>
                    </div>
                    <div className="col-md-6">
                      <Card className="card-bordered h-100 shadow-sm">
                        <div className="card-inner">
                          <div className="text-soft small text-uppercase mb-1">Selected Integration Context</div>
                          <div className="small">
                            <div>Integration: {formatPreviewValue(selectedPreviewIntegration?.integration_name)}</div>
                            <div>Last Sync: {formatPreviewDateTimeValue(selectedPreviewIntegration?.last_sync_at)}</div>
                            <div>Last Status: {formatPreviewValue(selectedPreviewIntegration?.last_sync_status)}</div>
                          </div>
                        </div>
                      </Card>
                    </div>
                  </div>

                  <div className="mb-4">
                    <h6 className="mb-2">All Remapped Tickets</h6>
                    {renderPreviewTable(allTickets, "No remapped tickets were returned.")}
                  </div>

                  <div className="mb-4">
                    <h6 className="mb-2 text-success">Valid Tickets</h6>
                    {renderPreviewTable(validTickets, "No valid tickets found in this preview.")}
                  </div>

                  <div className="mb-0">
                    <h6 className="mb-2 text-danger">Invalid Tickets</h6>
                    {renderPreviewTable(invalidTickets, "No invalid tickets found in this preview.")}
                  </div>
                </>
              )}

              {previewModalPage === 4 && !previewLoading && !previewData && (
                <Alert color="info" className="mb-0">
                  Run the preview first to see remapped ticket results here.
                </Alert>
              )}

              {/* Sync Results Section - shown after successful sync */}
              {syncCompleted && syncData && (
                <>
                  <hr className="my-4" />
                  <div className="bg-success text-white rounded p-3 mb-4">
                    <div className="d-flex align-items-center mb-2">
                      <span className="me-2">✓</span>
                      <strong>Sync Completed Successfully</strong>
                    </div>
                    <p className="mb-0 small">
                      {syncData?.total_tickets || 0} tickets processed. {successCreationTickets?.length || 0} created, {failCreationTickets?.length || 0} failed.
                    </p>
                  </div>

                  <div className="row g-3 mb-4">
                    <div className="col-md-4">
                      {previewSummaryCard("Total Processed", syncData?.total_tickets)}
                    </div>
                    <div className="col-md-4">
                      {previewSummaryCard("Successfully Created", successCreationTickets?.length || 0, "text-success")}
                    </div>
                    <div className="col-md-4">
                      {previewSummaryCard("Failed Creation", failCreationTickets?.length || 0, "text-danger")}
                    </div>
                  </div>

                  {successCreationTickets?.length > 0 && (
                    <div className="mb-4">
                      <h6 className="mb-2 text-success">Successfully Created Tickets</h6>
                      {renderPreviewTable(successCreationTickets, "No tickets were successfully created.")}
                    </div>
                  )}

                  {failCreationTickets?.length > 0 && (
                    <div className="mb-0">
                      <h6 className="mb-2 text-danger">Failed Creation Tickets</h6>
                      {renderPreviewTable(failCreationTickets, "No tickets failed during creation.")}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </ModalBody>
      </Modal>

      {/* Sync Confirmation Modal */}
      <Modal isOpen={openSyncConfirmModal} toggle={handleCloseSyncConfirmModal} className="modal-dialog-centered" size="md">
        <ModalHeader toggle={handleCloseSyncConfirmModal}>Confirm Sync</ModalHeader>
        <ModalBody>
          {syncError && (
            <Alert color="danger" className="mb-3">
              {syncError}
            </Alert>
          )}

          <div className="text-center mb-4">
            <div className="mb-3">
              <span className="fs-1 text-warning">⚠️</span>
            </div>
            <h5>Are you sure you want to sync these tickets?</h5>
            <p className="text-soft mb-0">
              This action will import <strong>{previewData?.total_valid_tickets_count || 0}</strong> valid tickets into the database.
              {previewData?.total_invalid_tickets_count > 0 && (
                <span className="d-block mt-1 text-danger">
                  {previewData?.total_invalid_tickets_count} invalid tickets will be skipped.
                </span>
              )}
            </p>
          </div>

          <div className="profile-ud-list mb-3">
            <ProfileDetailText label="Integration Name" text={selectedPreviewIntegrationName} />
            <ProfileDetailText label="CSV File" text={previewFile?.name || "--"} />
            <ProfileDetailText label="Valid Tickets to Import" text={previewData?.total_valid_tickets_count || 0} />
            <ProfileDetailText label="Invalid Tickets to Skip" text={previewData?.total_invalid_tickets_count || 0} />
            <ProfileDetailText
              label="Time Range"
              text={
                previewData?.time_in_range
                  ? `${formatPreviewDateTimeValue(previewData?.time_in_range?.start_date)} - ${formatPreviewDateTimeValue(previewData?.time_in_range?.end_date)}`
                  : "--"
              }
            />
          </div>

          <Alert color="info" className="mb-0">
            <strong>Note:</strong> This action cannot be undone. Tickets that already exist (based on external reference number) will not be duplicated.
          </Alert>
        </ModalBody>
        <ModalFooter className="px-0 pb-0">
          <Button color="light" onClick={handleCloseSyncConfirmModal} disabled={syncLoading}>
            Cancel
          </Button>
          <Button color="success" onClick={handleConfirmSyncTickets} disabled={syncLoading}>
            {syncLoading ? (
              <>
                <Spinner size="sm" className="me-2" />
                Syncing...
              </>
            ) : (
              "Confirm & Sync"
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default SpecificBranchLocation;
